import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ExternalScripts } from 'mm-ui-components';
import {
  getExternalScripts,
  getCanonicalUrl,
  getCmpId,
  getAuthorIds,
  getProperty,
  getPageType,
  getEdition,
  getTemplateName,
  getEditionEndpoint,
  getPropertyEndpoint,
  getExperiments,
  getIsFannation,
  getAuthors,
  getMainCategory,
  getVertical,
  getCustomTopic,
  getCustomVertical,
  getTitle,
  getTags,
  getAutoTags,
} from '../../store/config/config.selectors';
import { ExternalScripts as ExternalScriptsType } from '../../store/config/config.reducer';
import { getCWVScript } from '../../utilities/getCWVScript';
import { updateBCHemCookie } from '../../utilities/getUpdateBCCookie';
import { getCommercialTags } from '../../store/template/postPage/postPageAmsterdam/postPageAmsterdam.selectors';
import { getBlueConicScript } from '../../utilities/getBlueConicScript';
import { getSailthruScripts } from '../../utilities/getSailthruScripts';
import { getRetentionScript } from '../../utilities/getRetentionScript';
import { hasOAuth } from '../../utilities/hasOAuth';
import { getPermutiveScript } from '../../utilities/getPermutiveScript';
import { getChartbeatConversion } from '../../utilities/getChartbeatConversion';
import { hasPermutive } from '../../utilities/hasPermutive';
import { getUniqueTagList, isFansidedProperty, isSiProperty } from '../../store/config/config.utils';

export const CMP_EXCLUDED_PROPERTIES = [''];

const ONE_TRUST_DEFAULT_ID = '01948883-cdc5-73d6-a267-dd1f0a86d169';

function getOneTrustScripts(cmpId: string | null): ExternalScriptsType {
  const oneTrustID = cmpId || ONE_TRUST_DEFAULT_ID;

  const scripts: ExternalScriptsType = [];

  scripts.push({
    type: 'URL' as const,
    source: 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
    name: 'OTURL',
    attributes: {
      'data-domain-script': oneTrustID,
    },
    loadingType: 'blocking' as const,
  });

  scripts.push({
    type: 'URL' as const,
    source: 'https://cdn.cookielaw.org/opt-out/otCCPAiab.js',
    name: 'OTIAB',
    attributes: {
      'ccpa-opt-out-ids': 'C0004' as const,
      'ccpa-opt-out-geo': 'ca' as const,
      'ccpa-opt-out-lspa': 'true' as const,
    },
    loadingType: 'blocking' as const,
  });

  scripts.push({
    type: 'code' as const,
    source: `
      function OptanonWrapper() {
        const geoData = OneTrust.getGeolocationData?.();
        const isUS = (geoData?.country === "US");
        const groups = window.OnetrustActiveGroups || '';
        const consentGiven = groups.includes(',C0004,');

        const shouldLoadAutoBlockScript = !isUS || (isUS && !consentGiven);

        function updatePermutiveConsent() {
          if (typeof window.permutive !== 'undefined' && window.permutive.consent) {
            const currentGroups = window.OnetrustActiveGroups || '';
            const hasConsent = currentGroups.includes(',C0004,');
            if (hasConsent) {
              window.permutive.consent({ "opt_in": true, "token": "CONSENT_CAPTURED" });
            } else {
              window.permutive.consent({ "opt_in": false });
            }
          }
        }

        if (shouldLoadAutoBlockScript) {
          loadAutoBlockScript();
        } else {
          OneTrust.OnConsentChanged(function(eventData) {
            updatePermutiveConsent();
            loadAutoBlockScript();
            location.reload();
          });
        }

        function loadAutoBlockScript() {
          if (!document.getElementById("OTAutoBlock")) {
            var script = document.createElement("script");
            script.type = "text/javascript";
            script.src = "https://cdn-ukwest.onetrust.com/consent/${oneTrustID}/OtAutoBlock.js";
            script.id = "OTAutoBlock";
            document.head.appendChild(script);
          }
        }
      }
    `,
    name: 'OTInit',
    attributes: null,
    loadingType: 'blocking' as const,
  });

  return scripts;
}

export const HeadScripts: React.FunctionComponent = () => {
  const scripts = useSelector(getExternalScripts);
  const isRegistrationWall = useSelector(getCommercialTags)?.includes('registration-wall') || false;
  const canonicalUrl = useSelector(getCanonicalUrl);
  const edition = useSelector(getEdition);
  const editionEndpoint = useSelector(getEditionEndpoint);
  const cmpId = useSelector(getCmpId);
  const property = useSelector(getProperty);
  const authorIds = useSelector(getAuthorIds);
  const pageType = useSelector(getPageType);
  const templateName = useSelector(getTemplateName);
  const isPropertyExcluded = CMP_EXCLUDED_PROPERTIES.includes(property);
  const propertyEndpoint = useSelector(getPropertyEndpoint);
  const experiments = useSelector(getExperiments);
  const urlEndpoint = editionEndpoint || propertyEndpoint || '';
  const isFannation = useSelector(getIsFannation);
  const hasGoogleAuth = hasOAuth('google-oauth', propertyEndpoint, experiments, edition, isFannation);
  const hasMicrosoftAuth = hasOAuth('microsoft-oauth', propertyEndpoint, experiments, edition, isFannation);
  const isPermutiveSuppressed = experiments?.includes('permutive|suppress') || false;
  const authorNames = useSelector(getAuthors)?.map(author => author.name);
  const authors = authorNames?.join(',') || '';
  const mainCategory = useSelector(getMainCategory)?.displayName || '';
  const mainCategorySlug = useSelector(getMainCategory)?.slug || '';
  const customTopic = useSelector(getCustomTopic);
  const customVertical = useSelector(getCustomVertical);
  const vertical = useSelector(getVertical);
  // SI core sites don't contain a slash ex. www.si.com, non SI core sites do ex. www.si.com/es-us
  const isEdgeCase = !isFannation && urlEndpoint.includes('.si.com/');
  const ONSIDomain = urlEndpoint.includes('.si.com')
    ? `${edition.replace(/_/g, '-')}${isEdgeCase ? '' : '.on.si'}.com`
    : '';
  const domain = isFannation || isEdgeCase ? ONSIDomain : urlEndpoint;
  const isSIMX = edition === 'si_mexico';
  const isSI = isSiProperty(propertyEndpoint);
  const isFansided = isFansidedProperty(edition);
  const title = useSelector(getTitle);
  const tags = useSelector(getTags);
  const autoTags = useSelector(getAutoTags);
  const uniqueTags = getUniqueTagList(tags || [], autoTags || []);
  const autoTagsArray = autoTags?.map(tag => tag.tag) || [];

  if (!isPropertyExcluded) {
    scripts.unshift(...getOneTrustScripts(cmpId));
  }

  /** Retention Script */
  if (canonicalUrl || mainCategorySlug) {
    scripts.push(...getRetentionScript(canonicalUrl, mainCategorySlug, isFannation));
  }

  /** Chartbeat */
  const chartbeatData = `
    window._sf_async_config = {
      uid: 67224,
      domain: "${domain}",
      useCanonical: true,
      useCanonicalDomain: true,
      flickerControl: false,
      sections: "${mainCategory || pageType || ''}",
      authors: "${authors}",
    };
  `;

  scripts.push({
    type: 'code',
    source: chartbeatData,
    name: 'chartbeatData',
    attributes: null,
    loadingType: 'async',
  });

  scripts.push({
    type: 'URL',
    source: 'https://static.chartbeat.com/js/chartbeat_mab.js',
    attributes: null,
    loadingType: 'async',
    name: 'chartbeatHeadline',
  });

  scripts.push({
    type: 'URL',
    source: 'https://static.chartbeat.com/js/chartbeat.js',
    attributes: null,
    loadingType: 'async',
    name: 'chartbeat',
  });

  scripts.push({
    type: 'URL',
    source: 'https://static.chartbeat.com/js/subscriptions.js',
    attributes: null,
    loadingType: 'async',
    name: 'chartbeatConversion',
  });

  scripts.push({
    type: 'code',
    source: getChartbeatConversion,
    name: 'chartbeatConversionEvents',
    attributes: null,
    loadingType: 'async',
  });

  /** Google OAuth */
  if (hasGoogleAuth) {
    scripts.push({
      type: 'URL',
      source: 'https://accounts.google.com/gsi/client',
      name: 'googleOAuth',
      attributes: null,
      loadingType: 'async',
    });
  }

  /** Microsoft OAuth */
  if (hasMicrosoftAuth) {
    scripts.push({
      type: 'URL',
      source: 'https://edge-auth.microsoft.com/js/ms_auth_client.min.js',
      name: 'microsoftOAuth',
      attributes: null,
      loadingType: 'async',
    });
  }

  /** BlueConic */
  scripts.push({
    type: 'code',
    source: `
    window.mmBCPageData = {
      property: '${property}',
      authors: ${JSON.stringify(authorIds)},
      pageType: '${pageType}',
      templateName: '${templateName}',
      isRegistrationWall: '${isRegistrationWall}',
    };`,
    name: 'mmBCPageData',
    attributes: null,
    loadingType: 'blocking',
  });

  scripts.push(...getBlueConicScript(canonicalUrl, templateName, edition, urlEndpoint));

  /** CoreWebVitals */
  if (canonicalUrl) {
    const cwvScript = getCWVScript(canonicalUrl);
    if (cwvScript) {
      scripts.push(cwvScript);
    }
  }

  /** Sailthru */
  if (pageType === 'PostPage') {
    scripts.push(...getSailthruScripts(urlEndpoint, templateName));
  }

  /** Recirculation Widget */
  scripts.push({
    type: 'URL',
    source: 'https://assets.minutemediacdn.com/recirculation-widget/prod/mm-recirculation-injector.js',
    name: 'RecirculationWidget',
    attributes: null,
    loadingType: 'async',
  });

  scripts.push(updateBCHemCookie());

  useEffect(() => {
    // Explicitly return undefined to avoid linting errors
    if (isPermutiveSuppressed) return undefined;

    // Permutive script needs to be loaded client side because it requires geo information
    const w = window as any;
    const retries = 10;
    let count = 0;

    // Avoid race condition where OneTrust is not yet loaded
    const checkOneTrust = setInterval(() => {
      if (w.OneTrust) {
        clearInterval(checkOneTrust);

        // Should really use getCountryCode(), which would also mean we don't need retries,
        // but that cookie isn't working currently
        const oneTrustGeo = w.OneTrust?.getGeolocationData?.();
        const countryCode = oneTrustGeo?.country;
        const includePermutive = hasPermutive(countryCode, edition, isFansided, isSI);

        if (isSIMX || includePermutive) {
          const permutive = getPermutiveScript({
            authors: authorNames || [],
            brand: edition || '',
            keywords: tags || [],
            section: mainCategorySlug || '',
            subtitle: title || '',
            tags: uniqueTags,
            isSIMX,
            autoTags: autoTagsArray,
            vertical: customVertical || vertical || '',
            topic: customTopic || mainCategorySlug || '',
          });

          const permutiveSrc = isSIMX
            ? 'https://db98341e-60fd-4a55-a2a2-9cdc1d6cb3f8.edge.permutive.app/db98341e-60fd-4a55-a2a2-9cdc1d6cb3f8-web.js'
            : 'https://ad491600-d805-4a2c-870f-efe057881bc2.edge.permutive.app/ad491600-d805-4a2c-870f-efe057881bc2-web.js';

          if (document.getElementById('permutive-script-element')) return;

          const permutiveScriptElement = document.createElement('script');
          permutiveScriptElement.innerHTML = permutive;
          permutiveScriptElement.async = true;
          permutiveScriptElement.id = 'permutive-script-element';
          document.head.appendChild(permutiveScriptElement);

          const permutiveSrcElement = document.createElement('script');
          permutiveSrcElement.src = permutiveSrc;
          permutiveSrcElement.async = true;
          permutiveSrcElement.id = 'permutive-src-element';
          document.head.appendChild(permutiveSrcElement);

          // Set initial consent state after Permutive loads
          const setInitialPermutiveConsent = () => {
            if (typeof w.permutive !== 'undefined' && w.permutive.consent) {
              const groups = w.OnetrustActiveGroups || '';
              const hasConsent = groups.includes(',C0004,');
              if (hasConsent) {
                w.permutive.consent({ opt_in: true, token: 'CONSENT_CAPTURED' });
              } else {
                w.permutive.consent({ opt_in: false });
              }
            }
          };

          // Wait for Permutive to be ready, then set initial consent
          const checkPermutiveReady = setInterval(() => {
            if (typeof w.permutive !== 'undefined' && w.permutive.consent) {
              clearInterval(checkPermutiveReady);
              setInitialPermutiveConsent();
            }
          }, 100);

          // Clear the interval after 5 seconds to avoid infinite checking
          setTimeout(() => clearInterval(checkPermutiveReady), 5000);
        }
      } else {
        count += 1;
        if (count === retries) {
          clearInterval(checkOneTrust);
        }
      }
    }, 500);

    return () => clearInterval(checkOneTrust);
  });

  return <ExternalScripts scripts={scripts} />;
};
